import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { FirebaseService } from './firebase.service';
import { mergeMap, Observable } from 'rxjs';

export type ActivationKeySearchResultRow = {
  id: string;
  created_at: string;
  generation_trigger: string;
  product_code: string;
  expiration_date: string;
  request_ip?: string;
  user_agent?: string;
  referer?: string;
  unique_id?: string;
  tenant_account_number?: string;
  created_by_user_id?: string;
  created_by_email?: string;
  deactivated_at?: string;
};

export type ActivationKeysSearchResponse = {
  rows: ActivationKeySearchResultRow[];
  total_count: number;
  page_number: number;
  page_size: number;
  total_pages: number;
};

type ActivationKeysSearchBaseParams = {
  accountNumber?: string;
  createdAfter?: string;
  createdBefore?: string;
  createdBy?: string;
};

export type ActivationKeysSearchParams = ActivationKeysSearchBaseParams & {
  pageNumber: number;
  pageSize: number;
};

export type ActivationKeysExportParams = ActivationKeysSearchBaseParams;

export type SendCustomerActivationKeysEmailResponse = {
  transactionId: string;
  messageId: string;
  emails: string[];
  includedDesktopLicenseIds: string[];
  skippedDesktopLicenseIds: string[];
};

export type SentActivationKeyEmailLicense = {
  desktop_license_id: string;
  product_code: string;
  unique_id: string;
  product_title: string;
  expiration_date: string;
};

export type SentActivationKeyEmailStandaloneKey = {
  id: string;
  product_code: string;
  product_title: string;
  unique_id: string;
  expiration_date: string;
};

export type SentActivationKeyEmailsHistory = {
  id: string;
  tenant_id: string;
  emails: string[];
  sent_by_user_id: string;
  sent_by_email: string;
  sent_by_username: string;
  sent_at: string;
  sent_as_transfer: boolean;
  licenses: SentActivationKeyEmailLicense[];
  standalone_activation_keys?: SentActivationKeyEmailStandaloneKey[];
};

export type SentActivationKeyEmailsResponse = {
  sent_activation_key_emails: SentActivationKeyEmailsHistory[];
};

export type StandaloneActivationKey = {
  id: string;
  activation_key: string;
  product_code: string;
  product_id?: string;
  product_title?: string;
  expiration_date: string;
  user_id: string;
  tenant_id: string;
  unique_id: string;
  request_ip?: string;
  user_agent?: string;
  accept_language?: string;
  referer?: string;
  encrypted: boolean;
  created_at: string;
  created_by_email?: string;
};

export type StandaloneActivationKeysResponse = {
  standalone_activation_keys: StandaloneActivationKey[];
};

export type CreateStandaloneActivationKeyRequest = {
  tenant_id: string;
  product_code?: string;
  product_id?: string;
  product_title?: string;
  unique_id: string;
  expiration_date: string;
};

export type CreateStandaloneActivationKeyResponse = {
  standalone_activation_key: StandaloneActivationKey;
};

@Injectable({
  providedIn: 'root',
})
export class ActivationKeysService {
  private readonly baseUrl = environment.serverUrl + '/activation';

  constructor(
    private http: HttpClient,
    private firebaseService: FirebaseService
  ) {}

  search(
    params: ActivationKeysSearchParams
  ): Observable<ActivationKeysSearchResponse> {
    const httpParams = this.searchParams(params)
      .set('page_number', String(params.pageNumber))
      .set('page_size', String(params.pageSize));

    return this.firebaseService.idToken().pipe(
      mergeMap((token) =>
        this.http.get<ActivationKeysSearchResponse>(this.baseUrl + '/search', {
          headers: { Authorization: `Bearer ${token}` },
          params: httpParams,
        })
      )
    );
  }

  exportSearch(params: ActivationKeysExportParams) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) =>
        this.http.get(this.baseUrl + '/search', {
          headers: { Authorization: `Bearer ${token}` },
          params: this.searchParams(params).set('format', 'csv'),
          responseType: 'blob',
        })
      )
    );
  }

  private searchParams(params: ActivationKeysSearchBaseParams) {
    let httpParams = new HttpParams();
    if (params.accountNumber?.trim()) {
      httpParams = httpParams.set(
        'account_number',
        params.accountNumber.trim()
      );
    }
    if (params.createdAfter?.trim()) {
      httpParams = httpParams.set('created_after', params.createdAfter.trim());
    }
    if (params.createdBefore?.trim()) {
      httpParams = httpParams.set(
        'created_before',
        params.createdBefore.trim()
      );
    }
    if (params.createdBy?.trim()) {
      httpParams = httpParams.set('created_by', params.createdBy.trim());
    }
    return httpParams;
  }

  emailActivationKeys(
    tenantId: string,
    desktopLicenseIds: string[],
    standaloneActivationKeyIds: string[],
    emails: string[],
    isTransfer: boolean
  ): Observable<SendCustomerActivationKeysEmailResponse> {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) =>
        this.http.post<SendCustomerActivationKeysEmailResponse>(
          this.baseUrl + '/send-activation-keys-email',
          {
            tenant_id: tenantId,
            desktop_license_ids: desktopLicenseIds,
            standalone_activation_key_ids: standaloneActivationKeyIds,
            emails: emails,
            is_transfer: isTransfer,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      )
    );
  }

  getSentActivationKeyEmails(
    tenantId: string
  ): Observable<SentActivationKeyEmailsResponse> {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) =>
        this.http.get<SentActivationKeyEmailsResponse>(
          this.baseUrl + '/sent-activation-key-emails',
          {
            headers: { Authorization: `Bearer ${token}` },
            params: new HttpParams().set('tenant_id', tenantId),
          }
        )
      )
    );
  }

  listStandaloneActivationKeys(
    tenantId: string
  ): Observable<StandaloneActivationKeysResponse> {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) =>
        this.http.get<StandaloneActivationKeysResponse>(
          this.baseUrl + '/standalone',
          {
            headers: { Authorization: `Bearer ${token}` },
            params: new HttpParams().set('tenant_id', tenantId),
          }
        )
      )
    );
  }

  createStandaloneActivationKey(
    body: CreateStandaloneActivationKeyRequest
  ): Observable<CreateStandaloneActivationKeyResponse> {
    return this.firebaseService
      .idToken()
      .pipe(
        mergeMap((token) =>
          this.http.post<CreateStandaloneActivationKeyResponse>(
            this.baseUrl + '/standalone',
            body,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
  }
}
